import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { FileArrowUp, ClockCountdown } from "@phosphor-icons/react";

const COLOR = {
  accent: "#40B6A2",
  danger: "#FF3B30",
  green: "#00FF66",
  muted: "#6b89a8",
};

const fmt = (n) =>
  n != null
    ? n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const fmtDatetime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" });
};

function MovCell({ value }) {
  if (value == null) return <td className="p-3 text-right font-mono-data text-[#6b89a8]">—</td>;
  const color = value < 0 ? COLOR.danger : value > 0 ? COLOR.green : "#fff";
  return (
    <td className="p-3 text-right font-mono-data" style={{ color }}>
      {value < 0 ? "-" : value > 0 ? "+" : ""}
      {fmt(Math.abs(value))}
    </td>
  );
}

export default function Exposicao() {
  const { user } = useAuth();
  const fileRef = useRef(null);

  const hoje = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(hoje);
  const [demo, setDemo] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loadingDemo, setLoadingDemo] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);

  const loadDemo = useCallback(async () => {
    setLoadingDemo(true);
    try {
      const { data } = await api.get("/exposicao/demonstrativo", { params: { data: selectedDate } });
      setDemo(data);
    } catch {
      toast.error("Erro ao carregar demonstrativo");
    } finally {
      setLoadingDemo(false);
    }
  }, [selectedDate]);

  const loadHistorico = useCallback(async () => {
    try {
      const { data } = await api.get("/exposicao/historico");
      setHistorico(data);
    } catch {
      toast.error("Erro ao carregar histórico");
    }
  }, []);

  useEffect(() => {
    loadDemo();
  }, [loadDemo]);

  useEffect(() => {
    loadHistorico();
  }, [loadHistorico]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) {
      toast.error("Selecione um arquivo .xlsx");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      fd.append("data_referencia", selectedDate);
      const { data } = await api.post("/exposicao/importar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importado — ${data.total_rows} linha(s)`);
      await loadDemo();
      await loadHistorico();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar planilha");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  // Totals row for demonstrativo table
  const totalsBase = demo?.linhas?.reduce(
    (acc, l) => ({
      d1: acc.d1 != null && l.d1 != null ? acc.d1 + l.d1 : acc.d1 ?? l.d1,
      atual: acc.atual != null && l.atual != null ? acc.atual + l.atual : acc.atual ?? l.atual,
    }),
    { d1: null, atual: null }
  );
  // Movimentação do TOTAL é a diferença direta Atual-D1 (não a soma das
  // movimentações individuais, que já vêm com a regra de sinal aplicada).
  const totals = totalsBase && {
    ...totalsBase,
    mov:
      totalsBase.atual != null && totalsBase.d1 != null
        ? totalsBase.atual - totalsBase.d1
        : null,
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-semibold tracking-wide uppercase text-white">Exposição</h1>
            <p className="text-xs text-[#6b89a8] mt-0.5">Demonstrativo Geral de Movimentação</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleUpload}
            />
            <label className="text-xs text-[#6b89a8] uppercase tracking-wider">Data</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]"
            />
            <Button
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              style={{ background: COLOR.accent, color: "#061729" }}
              className="font-semibold"
            >
              <FileArrowUp className="w-4 h-4 mr-1.5" />
              {uploading ? "Importando…" : "Importar Planilha"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowHistorico((v) => !v)}
              className="border-[#123a5e] text-[#8aa9c9] hover:bg-[#0B2D4D]"
            >
              <ClockCountdown className="w-4 h-4 mr-1.5" />
              Histórico
            </Button>
          </div>
        </div>

        {/* Upload timestamps */}
        {demo && (demo.atual || demo.anterior) && (
          <div className="flex flex-wrap gap-4 text-xs text-[#6b89a8]">
            {demo.atual && (
              <span>
                <span className="text-[#8aa9c9]">Atual:</span>{" "}
                {demo.atual.data_referencia
                  ? `ref. ${demo.atual.data_referencia} · `
                  : ""}
                {fmtDatetime(demo.atual.uploaded_at)} — {demo.atual.filename}
              </span>
            )}
            {demo.anterior && (
              <span>
                <span className="text-[#8aa9c9]">D-1:</span>{" "}
                {demo.anterior.data_referencia
                  ? `ref. ${demo.anterior.data_referencia} · `
                  : ""}
                {fmtDatetime(demo.anterior.uploaded_at)} — {demo.anterior.filename}
              </span>
            )}
          </div>
        )}

        {/* Demonstrativo table */}
        <div className="rounded border border-[#123a5e] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#123a5e] text-[#6b89a8] text-xs uppercase tracking-wider">
                <th className="p-3 text-left font-medium">Tipo</th>
                <th className="p-3 text-right font-medium">Resultado D-1</th>
                <th className="p-3 text-right font-medium">Resultado Atual</th>
                <th className="p-3 text-right font-medium">Movimentação</th>
              </tr>
            </thead>
            <tbody>
              {loadingDemo ? (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-[#6b89a8] text-xs">
                    Carregando…
                  </td>
                </tr>
              ) : !demo?.linhas?.length ? (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-[#6b89a8] text-xs">
                    Nenhum upload encontrado. Importe uma planilha para começar.
                  </td>
                </tr>
              ) : (
                <>
                  {demo.linhas.map((l) => (
                    <tr
                      key={l.tipo}
                      className="border-b border-[#0d2e4a] hover:bg-[#0B2D4D]/40 transition-colors"
                    >
                      <td className="p-3 text-[#c8ddef]">{l.tipo}</td>
                      <td className="p-3 text-right font-mono-data text-[#8aa9c9]">
                        {fmt(l.d1)}
                      </td>
                      <td className="p-3 text-right font-mono-data text-white">{fmt(l.atual)}</td>
                      <MovCell value={l.movimentacao} />
                    </tr>
                  ))}
                  {/* Total row */}
                  {totals && (
                    <tr className="border-t-2 border-[#40B6A2]/40 bg-[#061729]">
                      <td className="p-3 font-semibold text-[#40B6A2] uppercase text-xs tracking-wider">
                        Total
                      </td>
                      <td className="p-3 text-right font-mono-data font-semibold text-[#8aa9c9]">
                        {fmt(totals.d1)}
                      </td>
                      <td className="p-3 text-right font-mono-data font-semibold text-white">
                        {fmt(totals.atual)}
                      </td>
                      <MovCell value={totals.mov} />
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Historico */}
        {showHistorico && (
          <div className="rounded border border-[#123a5e] overflow-x-auto">
            <div className="px-4 py-2.5 border-b border-[#123a5e]">
              <span className="text-xs uppercase tracking-wider text-[#6b89a8]">
                Histórico de Uploads
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#123a5e] text-[#6b89a8] text-xs uppercase tracking-wider">
                  <th className="p-3 text-left font-medium">Data / Hora</th>
                  <th className="p-3 text-left font-medium">Arquivo</th>
                  <th className="p-3 text-right font-medium">Linhas</th>
                </tr>
              </thead>
              <tbody>
                {historico.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-[#6b89a8] text-xs">
                      Nenhum upload registrado.
                    </td>
                  </tr>
                ) : (
                  historico.map((u, i) => (
                    <tr
                      key={u.id}
                      className={`border-b border-[#0d2e4a] hover:bg-[#0B2D4D]/40 transition-colors ${i === 0 ? "text-white" : "text-[#8aa9c9]"}`}
                    >
                      <td className="p-3 font-mono-data text-xs">{fmtDatetime(u.uploaded_at)}</td>
                      <td className="p-3 text-xs">{u.filename}</td>
                      <td className="p-3 text-right font-mono-data text-xs">{u.total_rows ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
